/* Wave 2 PDP enhancements for the pf-product-scroll section: mobile sticky
   add-to-cart bar and storage sibling price hints. Plain asset by design (no
   webpack). Survives AJAX product swaps by
   re-syncing on PUB_SUB_EVENTS.variantChange — HTMLUpdateUtility.viewTransition
   replaces the product-info element, so nothing here may hold references into
   it across swaps. */
(() => {
  "use strict";
  const getPilotRoot = () => document.querySelector('[data-pf-experience="pf_scroll_pilot"]');
  if (!getPilotRoot()) return;
  if (window.pfPilotScrollEnhancements) return;
  window.pfPilotScrollEnhancements = true;

  const getScrollProductInfo = () =>
    getPilotRoot()?.querySelector("pf-pilot-product-info[data-pf-scroll-product-info]");

  const getScrollSection = () => getPilotRoot();

  const getNativeProductInfo = () =>
    document
      .querySelector("product-info:not([data-pf-scroll-product-info]) .product__info-container")
      ?.closest("product-info");

  const normalizeOptionName = (value) =>
    String(value || "")
      .replace(/^\s*select\s+/i, "")
      .replace(/:\s*$/, "")
      .trim()
      .toLowerCase();

  const isDefaultTitle = (value) => normalizeOptionName(value) === "default title";

  const getFieldsetOptionName = (fieldset) => {
    const fromDataset = fieldset?.dataset?.optionName || fieldset?.dataset?.option;
    if (fromDataset) return String(fromDataset).trim();
    const labelText = fieldset
      ?.querySelector("legend.form__label, label.form__label")
      ?.textContent?.split(":")[0];
    return String(labelText || "")
      .replace(/^\s*select\s+/i, "")
      .trim();
  };

  const getConditionOptionPosition = () => {
    const section = getScrollSection();
    const declared = Number(
      section?.querySelector("[data-pf-condition-option-position]")?.dataset
        ?.pfConditionOptionPosition
    );
    if (declared > 0) return declared;
    const row = Array.from(section?.querySelectorAll("[data-pf-option-row]") || []).find((item) =>
      normalizeOptionName(item.dataset.optionName).includes("condition")
    );
    return Number(row?.dataset?.optionPosition) || 0;
  };

  const getSelectedVariant = () => {
    const script = getScrollProductInfo()?.querySelector(
      'variant-selects:not([data-upsell-isolated="true"]) [data-selected-variant]'
    );
    try {
      return script ? JSON.parse(script.textContent) : null;
    } catch {
      return null;
    }
  };

  const moneyFmt = new Intl.NumberFormat(document.documentElement.lang || "en", {
    style: "currency",
    currency: window.Shopify?.currency?.active || "USD",
  });
  const formatMoney = (cents) => moneyFmt.format(cents / 100).replace(/\.00$/, "");
  // The guided test (templates/product.guided-options.json): full prices, always with cents.
  const isGuidedPage = () =>
    Boolean(getPilotRoot()?.querySelector("[data-pf-adaptive-options][data-pf-guided-steps]"));
  const formatOptionMoney = (cents) =>
    isGuidedPage() ? moneyFmt.format(cents / 100) : formatMoney(cents);

  // sessionStorage-cached product JSON (10 min TTL); memoized in-flight so
  // concurrent callers share one fetch
  const PJSON_TTL = 10 * 60 * 1000;
  const pjsonMemo = new Map();
  const fetchProductJson = (url) => {
    const path = url.split("?")[0];
    if (pjsonMemo.has(path)) return pjsonMemo.get(path);
    const key = `pf-pilot:pjson:${path}`;
    try {
      const hit = JSON.parse(sessionStorage.getItem(key));
      if (hit && Date.now() - hit.t < PJSON_TTL) {
        const promise = Promise.resolve(hit.p);
        pjsonMemo.set(path, promise);
        return promise;
      }
    } catch {
      /* fall through to network */
    }
    const promise = fetch(`${path}.js`)
      .then((response) =>
        response.ok ? response.json() : Promise.reject(new Error(`HTTP ${response.status}`))
      )
      .then((product) => {
        try {
          sessionStorage.setItem(key, JSON.stringify({ t: Date.now(), p: product }));
        } catch {
          /* storage full — memo cache still applies */
        }
        return product;
      });
    pjsonMemo.set(path, promise);
    promise.catch(() => pjsonMemo.delete(path));
    return promise;
  };

  const hydrateInitialVariant = async () => {
    const productInfo = getScrollProductInfo();
    if (!productInfo) return null;
    const renderedVariant = getSelectedVariant();
    if (renderedVariant) return renderedVariant;
    const path = (productInfo.dataset.url || location.pathname).split("?")[0];
    const variantId =
      getScrollSection()?.querySelector("[data-pf-selected-variant-id]")?.dataset
        ?.pfSelectedVariantId || productInfo.querySelector('form input[name="id"]')?.value;
    try {
      const product = await fetchProductJson(path);
      return (
        product.variants.find((variant) => String(variant.id) === String(variantId)) ||
        product.variants.find((variant) => variant.available) ||
        product.variants[0] ||
        null
      );
    } catch {
      return null;
    }
  };

  // Variant-state bus: every feature re-syncs from the published variant object
  let lastVariant = null;
  const listeners = [];
  const onVariantState = (fn) => listeners.push(fn);
  const emit = () => {
    const variant = lastVariant || getSelectedVariant();
    listeners.forEach((fn) => {
      try {
        fn(variant);
      } catch {
        /* one feature failing must not break the others */
      }
    });
  };

  /* --- Mobile sticky add-to-cart bar --- */
  function initStickyAtc() {
    const productInfo = getScrollProductInfo();
    const sectionId = productInfo.dataset.section;

    const bar = document.createElement("div");
    bar.className = "pf-scroll-sticky-atc";
    bar.hidden = true;
    bar.innerHTML = `
      <div class="pf-scroll-sticky-atc__meta">
        <span class="pf-scroll-sticky-atc__price" data-sticky-price></span>
        <span class="pf-scroll-sticky-atc__condition" data-sticky-condition></span>
      </div>
      <button type="submit" name="add" form="product-form-${sectionId}"
        class="button button--primary pf-scroll-sticky-atc__submit" data-sticky-submit>
        <span data-sticky-label></span>
      </button>`;
    document.body.appendChild(bar);

    const price = bar.querySelector("[data-sticky-price]");
    const condition = bar.querySelector("[data-sticky-condition]");
    const submit = bar.querySelector("[data-sticky-submit]");
    const label = bar.querySelector("[data-sticky-label]");
    let stickyVariant = null;

    const setPurchaseMode = () => {
      submit.type = "submit";
      submit.name = "add";
      submit.setAttribute("form", `product-form-${sectionId}`);
      submit.disabled = false;
      delete submit.dataset.backInStockTrigger;
      label.textContent = window.variantStrings?.addToCart || "Add to cart";
    };

    const setSoldOutMode = () => {
      const backInStockReady = window.plugBackInStock?.ready === true;
      submit.type = "button";
      submit.removeAttribute("name");
      submit.removeAttribute("form");
      submit.disabled = !backInStockReady;
      if (backInStockReady) {
        submit.dataset.backInStockTrigger = "";
        label.textContent =
          window.plugBackInStock?.labels?.cta || window.variantStrings?.soldOut || "";
      } else {
        delete submit.dataset.backInStockTrigger;
        label.textContent = window.variantStrings?.soldOut || "";
      }
    };

    const sync = (variant) => {
      stickyVariant = variant;
      if (!variant) {
        submit.type = "button";
        submit.removeAttribute("name");
        submit.removeAttribute("form");
        submit.disabled = true;
        delete submit.dataset.backInStockTrigger;
        label.textContent = window.variantStrings?.unavailable || "Unavailable";
        price.textContent = "";
        condition.textContent = "";
        return;
      }
      price.textContent = formatOptionMoney(variant.price);
      const conditionPosition = getConditionOptionPosition();
      const conditionValue = conditionPosition ? variant.options?.[conditionPosition - 1] : "";
      condition.textContent =
        conditionValue && !isDefaultTitle(conditionValue) ? conditionValue : "";
      const soldOut = variant.available === false;
      if (soldOut) {
        setSoldOutMode();
      } else {
        setPurchaseMode();
      }
    };

    // Shared back-in-stock component contract. Keeping the handoff event-only
    // prevents this PF Scroll furniture from owning or duplicating signup UI.
    submit.addEventListener("click", (event) => {
      if (
        !stickyVariant ||
        stickyVariant.available !== false ||
        window.plugBackInStock?.ready !== true
      )
        return;
      event.preventDefault();
      document.dispatchEvent(
        new CustomEvent("plug:back-in-stock-open", {
          detail: {
            surface: "mobile_sticky",
            variantId: String(stickyVariant.id),
          },
        })
      );
    });
    document.addEventListener("plug:back-in-stock-ready", () => {
      sync(lastVariant || getSelectedVariant());
    });

    // Bar shows only once the shopper has scrolled past the reviews section,
    // and never while the real buy box is on screen (mobile-only via CSS) —
    // earlier in the page it just eats viewport without helping.
    // The guided test shows it earlier: as soon as the top title and price scroll away, so the
    // product, the price and the next step stay in view through the whole walk (desktop too, as a
    // strip under the header; see pf-scroll-pilot.css).
    let buyBoxVisible = false;
    let pastReviews = false;
    let pastIntro = false;
    const applyVisibility = () => {
      bar.hidden = buyBoxVisible || !(isGuidedPage() ? pastIntro : pastReviews);
    };

    let introObserver = null;
    const observeIntro = () => {
      const intro = getPilotRoot()?.querySelector(".pf-scroll-product-intro");
      introObserver?.disconnect();
      if (!intro) return;
      introObserver = new IntersectionObserver(([entry]) => {
        pastIntro = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
        applyVisibility();
      });
      introObserver.observe(intro);
    };

    let observer = null;
    const observeBuyBox = () => {
      const target = getPilotRoot()?.querySelector(
        "pf-pilot-product-info[data-pf-scroll-product-info] .pf-scroll-product-info__buy-buttons"
      );
      observer?.disconnect();
      if (!target) {
        buyBoxVisible = false;
        applyVisibility();
        return;
      }
      observer = new IntersectionObserver(
        ([entry]) => {
          buyBoxVisible = entry.isIntersecting;
          applyVisibility();
        },
        { rootMargin: "0px 0px -10% 0px" }
      );
      observer.observe(target);
    };

    let reviewsObserver = null;
    const observeReviews = (attempt = 0) => {
      if (attempt === 0) reviewsObserver?.disconnect();
      const reviews = document.querySelector(".junip-product-review");
      if (!reviews) {
        if (attempt < 15) {
          // Junip can hydrate late — retry before falling back
          setTimeout(() => observeReviews(attempt + 1), 1000);
        } else {
          pastReviews = true; // no reviews section on this page: old behavior
          applyVisibility();
        }
        return;
      }
      reviewsObserver?.disconnect();
      reviewsObserver = new IntersectionObserver(([entry]) => {
        pastReviews = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
        applyVisibility();
      });
      reviewsObserver.observe(reviews);
    };

    onVariantState((variant) => {
      sync(variant);
      observeBuyBox();
      observeReviews();
      observeIntro();
      // viewTransition removes the swapped-out node up to 500ms later — observe
      // again once the fresh node is the only one left
      setTimeout(observeBuyBox, 600);
      setTimeout(observeReviews, 600);
      setTimeout(observeIntro, 600);
    });
    sync(getSelectedVariant());
    observeBuyBox();
    observeReviews();
    observeIntro();
  }

  /* --- Contextual option pill prices --- */
  function initOptionPrices() {
    const getSelectedOptionsByName = (productInfo, variant = null) => {
      const selections = new Map();
      const source = productInfo.querySelector(
        'variant-selects:not([data-upsell-isolated="true"])'
      );
      source?.querySelectorAll("fieldset").forEach((fieldset, index) => {
        const name = normalizeOptionName(getFieldsetOptionName(fieldset));
        const checked = fieldset.querySelector('input[type="radio"]:checked');
        const selectedValue = variant?.options?.[index] || checked?.value;
        if (name && selectedValue) selections.set(name, selectedValue);
      });
      return selections;
    };

    const getProductOptionNames = (product) =>
      (product.options || []).map((option) =>
        normalizeOptionName(typeof option === "string" ? option : option?.name)
      );

    const variantMatchesDesiredOptions = (variant, optionNames, desiredOptions) =>
      optionNames.every((name, index) => {
        const desired = desiredOptions.get(name);
        return !desired || variant.options[index] === desired;
      });

    const resolveTargetVariant = (product, desiredOptions, preferredVariantId) => {
      const optionNames = getProductOptionNames(product);
      const preferred = preferredVariantId
        ? product.variants.find((variant) => String(variant.id) === String(preferredVariantId))
        : null;
      if (preferred && variantMatchesDesiredOptions(preferred, optionNames, desiredOptions)) {
        return preferred;
      }

      return (
        product.variants.find((variant) =>
          variantMatchesDesiredOptions(variant, optionNames, desiredOptions)
        ) || null
      );
    };

    const resolveStoragePriceVariant = (product, desiredOptions, preferredVariantId) => {
      const optionNames = getProductOptionNames(product);
      const conditionName = optionNames.find((name) => name.includes("condition"));
      if (!conditionName) {
        return {
          basis: "selected-options",
          conditionValue: "",
          variant: resolveTargetVariant(product, desiredOptions, preferredVariantId),
        };
      }

      const conditionIndex = optionNames.indexOf(conditionName);
      const baseDesiredOptions = new Map(desiredOptions);
      baseDesiredOptions.delete(conditionName);
      const matchingVariants = product.variants.filter((variant) =>
        variantMatchesDesiredOptions(variant, optionNames, baseDesiredOptions)
      );
      const availableVariants = matchingVariants.filter((variant) => variant.available !== false);
      const priceCandidates = availableVariants.length ? availableVariants : matchingVariants;
      const variant =
        priceCandidates.reduce((lowest, candidate) => {
          if (!lowest) return candidate;
          return Number(candidate.price) < Number(lowest.price) ? candidate : lowest;
        }, null) || resolveTargetVariant(product, desiredOptions, preferredVariantId);

      return {
        basis: availableVariants.length ? "lowest-available-condition" : "lowest-listed-condition",
        conditionValue: variant?.options?.[conditionIndex] || "",
        variant,
      };
    };

    const isUnavailableOption = (input, targetVariant) =>
      input.disabled ||
      input.getAttribute("aria-disabled") === "true" ||
      input.classList.contains("disabled") ||
      input.classList.contains("visually-disabled") ||
      targetVariant.available === false;

    const isUnavailableForOptionPrice = ({
      input,
      targetVariant,
      useResolvedAvailability = false,
    }) =>
      useResolvedAvailability
        ? targetVariant.available === false
        : isUnavailableOption(input, targetVariant);

    const renderOptionPrice = (
      {
        input,
        label,
        priceBasis = "",
        priceCondition = "",
        suppressWhenUnavailable = false,
        targetVariant,
        useResolvedAvailability = false,
      },
      { baselinePrice = null, incremental = false } = {}
    ) => {
      if (!input || !label || !targetVariant) return;
      let price = label.querySelector(".pf-pill-price");
      if (!price) {
        price = document.createElement("span");
        price.className = "pf-pill-price";
        label.appendChild(price);
      }

      const targetPrice = Number(targetVariant.price);
      const hasBaseline = Number.isFinite(baselinePrice);
      const isDelta = incremental && hasBaseline && targetPrice > baselinePrice;
      const isBase = incremental && hasBaseline && targetPrice === baselinePrice;
      const displayedCents = isDelta ? targetPrice - baselinePrice : targetPrice;
      const text = `${isDelta ? "+" : ""}${formatMoney(displayedCents)}`;
      if (price.textContent !== text) price.textContent = text;
      price.dataset.pfOptionPriceMode = isDelta ? "delta" : isBase ? "base" : "absolute";
      price.dataset.pfOptionPriceCents = String(displayedCents);
      price.dataset.pfOptionTargetPriceCents = String(targetPrice);
      if (hasBaseline) {
        price.dataset.pfOptionBasePriceCents = String(baselinePrice);
      } else {
        delete price.dataset.pfOptionBasePriceCents;
      }
      input.dataset.pfTargetVariantId = String(targetVariant.id);
      input.dataset.pfTargetPrice = String(targetPrice);
      input.dataset.pfTargetAvailable = targetVariant.available === false ? "false" : "true";
      if (priceBasis) {
        input.dataset.pfOptionPriceBasis = priceBasis;
        price.dataset.pfOptionPriceBasis = priceBasis;
      } else {
        delete input.dataset.pfOptionPriceBasis;
        delete price.dataset.pfOptionPriceBasis;
      }
      if (priceCondition) {
        input.dataset.pfOptionPriceCondition = priceCondition;
        price.dataset.pfOptionPriceCondition = priceCondition;
      } else {
        delete input.dataset.pfOptionPriceCondition;
        delete price.dataset.pfOptionPriceCondition;
      }
      const priceUnavailable = isUnavailableForOptionPrice({
        input,
        targetVariant,
        useResolvedAvailability,
      });
      price.hidden = suppressWhenUnavailable && priceUnavailable;
      price.classList.toggle("pf-pill-price--soldout", priceUnavailable);
    };

    let decorateSequence = 0;
    const decorate = async () => {
      decorateSequence += 1;
      const sequence = decorateSequence;
      const productInfo = getScrollProductInfo();
      if (!productInfo) return;
      const currentPath = (productInfo.dataset.url || location.pathname).split("?")[0];
      const selectedOptions = getSelectedOptionsByName(
        getNativeProductInfo() || productInfo,
        lastVariant || getSelectedVariant()
      );
      const fieldsets =
        getPilotRoot()?.querySelectorAll(
          ".pf-product-scroll__variant-option fieldset.product-form__input--pill"
        ) || [];

      const resolvedFieldsets = await Promise.all(
        Array.from(fieldsets).map(async (fieldset) => {
          const optionName = normalizeOptionName(getFieldsetOptionName(fieldset));
          if (!optionName || optionName.includes("color") || optionName.includes("colour")) return;
          const isStorageOption = optionName.includes("storage");
          const inputs = Array.from(fieldset.querySelectorAll('input[type="radio"]'));
          const resolvedOptions = await Promise.all(
            inputs.map(async (input) => {
              const path = (input.dataset.productUrl || currentPath).split("?")[0];
              const label = fieldset.querySelector(`label[for="${CSS.escape(input.id)}"]`);
              if (!path || !label) return;
              const desiredOptions = new Map(selectedOptions);
              desiredOptions.set(optionName, input.value);
              try {
                const targetProduct = await fetchProductJson(path);
                const resolution = isStorageOption
                  ? resolveStoragePriceVariant(
                      targetProduct,
                      desiredOptions,
                      input.dataset.pfTargetVariantId
                    )
                  : {
                      basis: "selected-options",
                      conditionValue: "",
                      variant: resolveTargetVariant(
                        targetProduct,
                        desiredOptions,
                        input.dataset.pfTargetVariantId
                      ),
                    };
                return resolution.variant
                  ? {
                      input,
                      label,
                      priceBasis: resolution.basis,
                      priceCondition: resolution.conditionValue,
                      suppressWhenUnavailable: optionName.includes("condition"),
                      targetVariant: resolution.variant,
                      useResolvedAvailability: isStorageOption,
                    }
                  : null;
              } catch {
                /* server-rendered price remains authoritative */
                return null;
              }
            })
          );
          return {
            fieldset,
            incremental: optionName.includes("storage") || optionName.includes("condition"),
            resolvedOptions: resolvedOptions.filter(Boolean),
          };
        })
      );

      if (sequence !== decorateSequence) return;
      resolvedFieldsets.filter(Boolean).forEach(({ fieldset, incremental, resolvedOptions }) => {
        if (!fieldset.isConnected) return;
        const availablePrices = resolvedOptions
          .filter((resolvedOption) => !isUnavailableForOptionPrice(resolvedOption))
          .map(({ targetVariant }) => Number(targetVariant.price))
          .filter(Number.isFinite);
        const baselinePrice = availablePrices.length ? Math.min(...availablePrices) : null;

        resolvedOptions.forEach((resolvedOption) => {
          renderOptionPrice(resolvedOption, { baselinePrice, incremental });
        });
      });
    };

    // Full-main combined-listing swaps replace option targets, so observe each
    // newly inserted target once while keeping the global listener singleton.
    let queued = false;
    const observedTargets = new WeakSet();
    const cloneObserver = new MutationObserver(() => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        decorate();
      });
    });
    const observeOptionTargets = () => {
      getPilotRoot()
        ?.querySelectorAll(".pf-product-scroll__variant-option")
        .forEach((target) => {
          if (observedTargets.has(target)) return;
          observedTargets.add(target);
          cloneObserver.observe(target, { childList: true, subtree: true });
        });
    };
    onVariantState(() => {
      observeOptionTargets();
      setTimeout(decorate, 650);
    });
    observeOptionTargets();
    decorate();
  }

  const init = async () => {
    if (!getScrollProductInfo()) return; // not a pf-scroll PDP
    lastVariant = getSelectedVariant();
    if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
      subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        lastVariant = event?.data?.variant ?? null;
        // double rAF: let HTMLUpdateUtility insert the new product-info first
        requestAnimationFrame(() => requestAnimationFrame(emit));
      });
    }
    document.addEventListener("pf:product-context-rebuilt", (event) => {
      lastVariant = event.detail?.variant || lastVariant;
      requestAnimationFrame(() => requestAnimationFrame(emit));
    });
    if (!lastVariant) {
      const hydratedVariant = await hydrateInitialVariant();
      if (!lastVariant) lastVariant = hydratedVariant;
    }
    initStickyAtc();
    initOptionPrices();
    emit();
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
